import AuthorizationService from '@/services/AuthorizationService';
import reactQueryClient, {
  persister,
} from '@/services/ReactQueryClientService';
import { auth } from '@/utils/firebase';
import { logError, logInfo } from '@/utils/logger';
import firebase from 'firebase/app';
import {
  ApplicationVerifier,
  applyActionCode,
  ConfirmationResult,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  TwitterAuthProvider,
  User,
  verifyPasswordResetCode,
} from 'firebase/auth';
interface CustomTwitterUser extends User {
  reloadUserInfo?: {
    screenName?: string;
  };
}
const AuthService = {
  handlePostLogin: async (user: User) => {
    try {
      logInfo(
        'User logged in, ReferralProvider will handle referral code',
        'handlePostLogin',
      );
    } catch (error: any) {
      logError(error, 'handlePostLogin');
    }
  },
  checkAuthMethodsForEmail: async (email: string) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods;
    } catch (error: any) {
      logError(error, 'checkAuthMethodsForEmail');
      return [];
    }
  },
  signInWithGoogle: async () => {
    try {
      const googleProvider = new GoogleAuthProvider();
      const res = await signInWithPopup(auth, googleProvider);
      const user = res.user;
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'signInWithGoogle');
      if (error.code === 'auth/account-exists-with-different-credential') {
        throw {
          code: error.code,
          error: error.message,
          email: error.customData?.email,
          isAuthMethodConflict: true,
        };
      }
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  signInWithTwitter: async () => {
    try {
      const twitterProvider = new TwitterAuthProvider();
      twitterProvider.setCustomParameters({
        include_email: 'true',
      });
      const res = await signInWithPopup(auth, twitterProvider);
      const user = res.user as CustomTwitterUser;
      logInfo('Twitter sign-in successful', 'signInWithTwitter', user);
      localStorage.setItem(
        'twitterScreenName',
        user.reloadUserInfo?.screenName || '',
      );
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'signInWithTwitter');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  signInWithFacebook: async () => {
    try {
      const facebookProvider = new FacebookAuthProvider();
      const res = await signInWithPopup(auth, facebookProvider);
      const user = res.user;
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'signInWithFacebook');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  loginWithEmailAndPassword: async (email: string, password: string) => {
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      const user = res.user;
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'loginWithEmailAndPassword');
      if (
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/invalid-credential'
      ) {
        const methods = await AuthService.checkAuthMethodsForEmail(email);
        if (methods.includes('google.com')) {
          throw {
            code: error.code,
            error: error.message,
            isGoogleAccount: true,
          };
        }
      }
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  signUp: async (email: string, password: string) => {
    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);
      const user = res.user;
      await sendEmailVerification(user);
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'signUp');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  isEmailVerified: async () => {
    const user = auth.currentUser;
    if (user) {
      await user.reload();
      return user.emailVerified || false;
    }
    return false;
  },
  resendVerificationEmail: async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user);
        return true;
      }
      return false;
    } catch (error: any) {
      logError(error, 'resendVerificationEmail');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  applyActionCode: async (oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      return true;
    } catch (error: any) {
      logError(error, 'applyActionCode');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  signInWithPhoneNumber: async (
    phoneNumber: string,
    appVerifier: ApplicationVerifier,
  ) => {
    try {
      const res = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      return res;
    } catch (error: any) {
      logError(error, 'signInWithPhoneNumber');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  confirmOTPCode: async (
    otpCode: string,
    confirmationResult: ConfirmationResult,
  ) => {
    try {
      const res = await confirmationResult.confirm(otpCode);
      const user = res.user;
      await AuthService.handlePostLogin(user);
      return user;
    } catch (error: any) {
      logError(error, 'confirmOTPCode');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  resetPassword: async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      logError(error, 'resetPassword');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  verifyCode: async (oobCode: string) => {
    try {
      await verifyPasswordResetCode(auth, oobCode);
    } catch (error: any) {
      logError(error, 'verifyCode');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  confirmPassword: async (oobCode: string, newPassword: string) => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error: any) {
      logError(error, 'confirmPassword');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
  logout: async () => {
    try {
      await auth.signOut();
      reactQueryClient.clear();
      if (persister) {
        await persister.removeClient();
      }
      localStorage.clear();
      AuthorizationService.setAuthorized();
    } catch (error: any) {
      logError(error, 'logout');
      throw {
        code: error.code,
        error: error.message,
      };
    }
  },
};
export type FirebaseError = typeof firebase.FirebaseError;
export default AuthService;
